import React, { useState } from 'react'
import {
  Alert,
  Button,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  IconButton,
} from '@mui/material'
import { AddCircle, RemoveCircle } from '@mui/icons-material'
import API from '../../../services/API'
import { useNavigate, useSearchParams } from 'react-router-dom'
import PageHeader from '../../kit/PageHeader'
import {
  HAZARD_TYPE_OPTIONS as hazardTypeOptions,
  LIKELIHOOD_OPTIONS as likelihoodOptions,
  MODULE_OPTIONS as moduleOptions,
  REGISTERS,
  REGISTER_OPTIONS,
  SEVERITY_OPTIONS as severityOptions,
  SOURCE_OPTIONS as sourceOptions,
  riskRatingLabel,
} from './constants'

const CreateHazardLogForm = () => {
  const [searchParams] = useSearchParams()
  const initialRegister = REGISTER_OPTIONS.includes(searchParams.get('register'))
    ? searchParams.get('register')
    : 'Clinical'

  const [register, setRegister] = useState(initialRegister)
  const [title, setTitle] = useState('')
  const [module, setModule] = useState('')
  const [hazardType, setHazardType] = useState('Clinical Safety')
  const [source, setSource] = useState('')
  const [description, setDescription] = useState('')
  const [cause, setCause] = useState([''])
  const [clinicalImpact, setClinicalImpact] = useState([''])
  const [controlMeasures, setControlMeasures] = useState([
    { type: 'existing', control: '', description: '' },
  ])
  const [initialRisk, setInitialRisk] = useState({
    likelihood: '',
    severity: '',
    riskRating: '',
  })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const navigate = useNavigate()

  const handleSubmit = () => {
    setError('')

    if (!title.trim() || !description.trim() || !module) {
      setError('Title, description, and module are required')
      return
    }

    setSaving(true)

    API.post('/admincrm/clinical-risk/hazard-log', {
      register,
      title,
      description,
      module,
      hazardType,
      source,
      cause: cause.filter((r) => r.trim() !== ''),
      clinicalImpact: clinicalImpact.filter((r) => r.trim() !== ''),
      controlMeasures,
      initialRisk,
      status: 'Open',
    })
      .then((res) => {
        if (res.status === 201) {
          navigate(`/clinical-risk/${res.data._id}`)
        }
      })
      .catch((err) => {
        console.error('Error saving hazard', err)
        setError('Unable to save hazard')
      })
      .finally(() => setSaving(false))
  }

  return (
    <Stack gap={2.5}>
      <PageHeader
        eyebrow="Clinical risk"
        title="New hazard"
        subtitle={`Will be added to the ${REGISTERS[register].label}.`}
        back={REGISTERS[register].path}
      />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: '1px solid #E2E8F0',
          borderRadius: 3,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Register"
              value={register}
              helperText={
                register === 'Codebase'
                  ? 'Found by examining the software: cite file and line evidence in Notes.'
                  : 'About AmbuNet as a product or organisation.'
              }
              onChange={(e) => setRegister(e.target.value)}
            >
              {REGISTER_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {REGISTERS[opt].label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Module"
              value={module}
              onChange={(e) => setModule(e.target.value)}
            >
              {moduleOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Hazard Type"
              value={hazardType}
              onChange={(e) => setHazardType(e.target.value)}
            >
              {hazardTypeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              <MenuItem value="">
                <em>Not specified</em>
              </MenuItem>
              {sourceOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography fontWeight={600}>Cause</Typography>
            {cause.map((item, idx) => (
              <Stack direction="row" spacing={1} key={idx} alignItems="center" mt={1}>
                <TextField
                  fullWidth
                  label={`Cause ${idx + 1}`}
                  value={item}
                  onChange={(e) => {
                    const updated = [...cause]
                    updated[idx] = e.target.value
                    setCause(updated)
                  }}
                />
                <IconButton onClick={() => setCause(cause.filter((_, i) => i !== idx))}>
                  <RemoveCircle />
                </IconButton>
              </Stack>
            ))}
            <Button startIcon={<AddCircle />} onClick={() => setCause([...cause, ''])}>
              Add Cause
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Typography fontWeight={600}>Clinical Impact</Typography>
            {clinicalImpact.map((item, idx) => (
              <Stack direction="row" spacing={1} key={idx} alignItems="center" mt={1}>
                <TextField
                  fullWidth
                  label={`Impact ${idx + 1}`}
                  value={item}
                  onChange={(e) => {
                    const updated = [...clinicalImpact]
                    updated[idx] = e.target.value
                    setClinicalImpact(updated)
                  }}
                />
                <IconButton
                  onClick={() => setClinicalImpact(clinicalImpact.filter((_, i) => i !== idx))}
                >
                  <RemoveCircle />
                </IconButton>
              </Stack>
            ))}
            <Button
              startIcon={<AddCircle />}
              onClick={() => setClinicalImpact([...clinicalImpact, ''])}
            >
              Add Impact
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Typography fontWeight={600}>Control Measures</Typography>
            {controlMeasures.map((cm, idx) => (
              <Stack direction="row" spacing={1} key={idx} mt={1} width="100%">
                <TextField
                  select
                  label="Type"
                  sx={{ minWidth: 140 }}
                  value={cm.type || 'existing'}
                  onChange={(e) => {
                    const updated = [...controlMeasures]
                    updated[idx].type = e.target.value
                    setControlMeasures(updated)
                  }}
                >
                  <MenuItem value="existing">Existing</MenuItem>
                  <MenuItem value="planned">Planned</MenuItem>
                </TextField>
                <TextField
                  label="Control"
                  fullWidth
                  value={cm.control}
                  onChange={(e) => {
                    const updated = [...controlMeasures]
                    updated[idx].control = e.target.value
                    setControlMeasures(updated)
                  }}
                />
                <TextField
                  label="Description"
                  value={cm.description}
                  fullWidth
                  onChange={(e) => {
                    const updated = [...controlMeasures]
                    updated[idx].description = e.target.value
                    setControlMeasures(updated)
                  }}
                />
                <IconButton
                  onClick={() => setControlMeasures(controlMeasures.filter((_, i) => i !== idx))}
                >
                  <RemoveCircle />
                </IconButton>
              </Stack>
            ))}
            <Button
              startIcon={<AddCircle />}
              onClick={() =>
                setControlMeasures([
                  ...controlMeasures,
                  { type: 'existing', control: '', description: '' },
                ])
              }
            >
              Add Control Measure
            </Button>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Likelihood"
              value={initialRisk.likelihood}
              onChange={(e) => setInitialRisk({ ...initialRisk, likelihood: e.target.value })}
            >
              {likelihoodOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Severity"
              value={initialRisk.severity}
              onChange={(e) => setInitialRisk({ ...initialRisk, severity: e.target.value })}
            >
              {severityOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Risk Rating"
              value={riskRatingLabel(initialRisk)}
              disabled
              placeholder="Pick likelihood and severity"
              helperText="Set automatically from the CRMP risk matrix"
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Hazard'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  )
}

export default CreateHazardLogForm
